/**
 * DILTS Logical Levels — personal story & branding (v0.1).
 *
 * Robert Dilts' Neurological Levels / Logical Levels of Change.
 * Helps people articulate their story at each level and speak at the
 * depth the conversation requires.
 */

export type DiltsLevelId = 1 | 2 | 3 | 4 | 5 | 6;

export interface DiltsLevel {
  id: DiltsLevelId;
  title: string;
  shortTitle: string;
  questionWord: string;
  prompt: string;
  brandingHint: string;
  commonMistake: string;
  example: string;
}

export const DILTS_LEVELS: DiltsLevel[] = [
  {
    id: 1,
    title: 'Environment',
    shortTitle: 'Environment',
    questionWord: 'Where? When? With whom?',
    prompt: 'Where do you work? What are the external conditions and context?',
    brandingHint: 'Ground your story in situational facts — not excuses.',
    commonMistake: 'Over-focusing on external conditions instead of what you can control.',
    example:
      'I work at the intersection of AI regulation and policy, advising enterprise teams navigating new compliance frameworks.',
  },
  {
    id: 2,
    title: 'Behaviors',
    shortTitle: 'Behavior',
    questionWord: 'What?',
    prompt: 'What do you actually do? What actions do you take daily?',
    brandingHint: 'Name concrete actions — not a job title dressed up as a verb.',
    commonMistake: 'Listing activities without differentiation — tasks alone are forgettable.',
    example:
      'I advise policymakers and industry leaders on responsible AI deployment and run executive workshops on governance.',
  },
  {
    id: 3,
    title: 'Skills & Capabilities',
    shortTitle: 'Capabilities',
    questionWord: 'How?',
    prompt: 'What unique strengths do you bring? What makes you effective?',
    brandingHint: 'Articulate high-leverage capabilities — what makes you different, not generic.',
    commonMistake: 'Stopping here — most people never get past skills when asked to introduce themselves.',
    example:
      'I combine deep technical expertise with regulatory knowledge and translate complexity into decisions leaders can act on.',
  },
  {
    id: 4,
    title: 'Values & Beliefs',
    shortTitle: 'Values',
    questionWord: 'Why?',
    prompt: 'What principles drive you? What do you stand for?',
    brandingHint: 'This is what resonates — name the belief, not the buzzword.',
    commonMistake: 'Never articulating this clearly, even though it is what people remember.',
    example:
      'I believe AI should be transparent and accountable to society — clarity is the foundation of effective leadership.',
  },
  {
    id: 5,
    title: 'Identity',
    shortTitle: 'Identity',
    questionWord: 'Who?',
    prompt: 'Who are you, beyond your job title?',
    brandingHint: 'Define positioning — not a role on an org chart.',
    commonMistake: 'Defaulting to job titles instead of a distinct professional identity.',
    example:
      'I am an AI ethics strategist who helps organizations align innovation with human rights and democratic values.',
  },
  {
    id: 6,
    title: 'Mission / Purpose',
    shortTitle: 'Purpose',
    questionWord: 'What for?',
    prompt: 'What impact do you want to make in the world? What are you in service of?',
    brandingHint: 'Optional but powerful — connect to something bigger than yourself.',
    commonMistake: 'Feeling abstract or lost here — start with who benefits, not a grand slogan.',
    example:
      'I aim to ensure AI development aligns with human rights so leaders make strategic choices, not reactive ones.',
  },
];

export type DiltsStoryStep =
  | 'intro'
  | 'level-1'
  | 'level-2'
  | 'level-3'
  | 'level-4'
  | 'level-5'
  | 'level-6'
  | 'elevate'
  | 'synthesize'
  | 'results';

export const DILTS_STORY_STEPS: { id: DiltsStoryStep; label: string; short: string }[] = [
  { id: 'intro', label: 'Why levels matter', short: 'Start' },
  { id: 'level-1', label: 'Environment', short: 'L1' },
  { id: 'level-2', label: 'Behaviors', short: 'L2' },
  { id: 'level-3', label: 'Capabilities', short: 'L3' },
  { id: 'level-4', label: 'Values', short: 'L4' },
  { id: 'level-5', label: 'Identity', short: 'L5' },
  { id: 'level-6', label: 'Purpose', short: 'L6' },
  { id: 'elevate', label: 'Move between levels', short: 'Elevate' },
  { id: 'synthesize', label: 'Shape your pitches', short: 'Pitch' },
  { id: 'results', label: 'Your story stack', short: 'Done' },
];

export interface DiltsElevateNotes {
  whyAnswer: string;
  howAnswer: string;
  whoAnswer: string;
}

export interface DiltsPitchDrafts {
  behaviorPitch: string;
  valuesPitch: string;
  fullPitch: string;
}

export interface DiltsStoryState {
  levels: Record<DiltsLevelId, string>;
  elevate: DiltsElevateNotes;
  pitches: DiltsPitchDrafts;
  completedAt?: string;
}

export const ELEVATE_PROMPTS = [
  {
    id: 'why' as const,
    move: 'Up to Values',
    question: '"Why do you do that?" — what belief or principle sits underneath?',
    field: 'whyAnswer' as const,
  },
  {
    id: 'how' as const,
    move: 'Down to Behavior',
    question: '"How do you do that?" — what would someone see you doing tomorrow?',
    field: 'howAnswer' as const,
  },
  {
    id: 'who' as const,
    move: 'Up to Identity',
    question: '"Who are you beyond your job title?" — how would you introduce yourself without a role name?',
    field: 'whoAnswer' as const,
  },
];

export function emptyLevels(): Record<DiltsLevelId, string> {
  return { 1: '', 2: '', 3: '', 4: '', 5: '', 6: '' };
}

export function createDefaultDiltsStoryState(): DiltsStoryState {
  return {
    levels: emptyLevels(),
    elevate: { whyAnswer: '', howAnswer: '', whoAnswer: '' },
    pitches: { behaviorPitch: '', valuesPitch: '', fullPitch: '' },
  };
}

export function stepIndex(step: DiltsStoryStep): number {
  return DILTS_STORY_STEPS.findIndex(s => s.id === step);
}

export function nextStep(step: DiltsStoryStep): DiltsStoryStep | null {
  const idx = stepIndex(step);
  if (idx < 0 || idx >= DILTS_STORY_STEPS.length - 1) return null;
  return DILTS_STORY_STEPS[idx + 1].id;
}

export function prevStep(step: DiltsStoryStep): DiltsStoryStep | null {
  const idx = stepIndex(step);
  if (idx <= 0) return null;
  return DILTS_STORY_STEPS[idx - 1].id;
}

export function levelForStep(step: DiltsStoryStep): DiltsLevel | null {
  const match = step.match(/^level-(\d)$/);
  if (!match) return null;
  const id = Number(match[1]) as DiltsLevelId;
  return DILTS_LEVELS.find(l => l.id === id) ?? null;
}

export function canAdvance(step: DiltsStoryStep, state: DiltsStoryState): boolean {
  switch (step) {
    case 'intro':
      return true;
    case 'level-1':
    case 'level-2':
    case 'level-3':
    case 'level-4':
    case 'level-5':
    case 'level-6': {
      const level = levelForStep(step);
      if (!level) return false;
      return state.levels[level.id].trim().length >= 12;
    }
    case 'elevate':
      return (
        state.elevate.whyAnswer.trim().length >= 8 &&
        state.elevate.howAnswer.trim().length >= 8 &&
        state.elevate.whoAnswer.trim().length >= 8
      );
    case 'synthesize':
      return (
        state.pitches.behaviorPitch.trim().length >= 20 &&
        state.pitches.valuesPitch.trim().length >= 20
      );
    case 'results':
      return true;
    default:
      return false;
  }
}

function ensureSentence(text: string): string {
  const t = text.trim();
  if (!t) return '';
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

export function suggestBehaviorPitch(state: DiltsStoryState): string {
  const env = state.levels[1].trim();
  const behavior = state.levels[2].trim();
  const skills = state.levels[3].trim();
  if (!behavior) return '';
  const parts: string[] = [];
  if (env) parts.push(ensureSentence(env));
  parts.push(ensureSentence(behavior));
  if (skills) parts.push(ensureSentence(skills));
  return parts.join(' ');
}

export function suggestValuesPitch(state: DiltsStoryState): string {
  const values = state.levels[4].trim();
  const identity = state.levels[5].trim();
  const parts: string[] = [];
  if (values) {
    const v = values.replace(/^I believe\s+/i, '');
    parts.push(ensureSentence(`I believe ${v}`));
  }
  if (identity) {
    const id = identity.replace(/^I am\s+/i, '');
    parts.push(ensureSentence(`I am ${id}`));
  }
  return parts.join(' ');
}

export function suggestFullPitch(state: DiltsStoryState): string {
  const values = suggestValuesPitch(state);
  const purpose = state.levels[6].trim();
  const behavior = state.levels[2].trim();
  const parts: string[] = [];
  if (values) parts.push(values);
  if (purpose) {
    const p = purpose.replace(/^I (aim to|want to)\s+/i, '');
    parts.push(ensureSentence(`I aim to ${p}`));
  }
  if (behavior && parts.length < 2) parts.push(ensureSentence(behavior));
  return parts.join(' ');
}

export function autoFillPitches(state: DiltsStoryState): DiltsPitchDrafts {
  return {
    behaviorPitch: suggestBehaviorPitch(state),
    valuesPitch: suggestValuesPitch(state),
    fullPitch: suggestFullPitch(state),
  };
}

export function countFilledLevels(state: DiltsStoryState): number {
  return DILTS_LEVELS.filter(l => state.levels[l.id].trim().length >= 12).length;
}
